import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import type { Feature, PlanTier } from '@/context/plan-constraint-utils'

/**
 * The one place a refused action asks for the upgrade pitch.
 *
 * Modelled on the legacy app's named-modal registry: a call site says WHICH
 * gate refused and hands over any numbers the copy needs, and a single host
 * decides what to render. Call sites never import the dialog, never pick copy,
 * and never hardcode a plan name.
 *
 * Deliberately no cooldown or dismissal memory. Every prompt is raised by an
 * explicit user action, so reopening on a retry is correct: the user asked
 * again. A prompt raised WITHOUT a click (a server refusal arriving on its own)
 * is the only case that would need a limit, and none exists yet.
 */

/** Why the gate refused, which decides how the pitch opens. */
export type UpgradeReason =
  /** The plan does not include the feature at all. */
  | 'locked'
  /** The plan includes it but the allowance is spent. */
  | 'limit'

export interface UpgradePromptContext {
  /** The allowance that was hit, for `limit` prompts. */
  limit?: number
  /** What the allowance counts, e.g. "Scanner". Singular. */
  label?: string
}

export interface UpgradePromptRequest {
  feature: Feature
  reason?: UpgradeReason
  /**
   * The tier to pitch. Defaults to the feature's own requirement, which is
   * what a `locked` prompt wants. A `limit` prompt overrides it, because the
   * plan already HAS the feature and the pitch is the next tier up.
   */
  requiredTier?: PlanTier
  context?: UpgradePromptContext
}

interface UpgradePromptState {
  request: UpgradePromptRequest | null
  openUpgradePrompt: (request: UpgradePromptRequest) => void
  closeUpgradePrompt: () => void
}

export const useUpgradePromptStore = create<UpgradePromptState>()(
  devtools(
    (set) => ({
      request: null,
      openUpgradePrompt: (request) => set({ request }, false, 'openUpgradePrompt'),
      closeUpgradePrompt: () => set({ request: null }, false, 'closeUpgradePrompt'),
    }),
    { name: 'zustand/upgrade-prompt', store: 'upgrade-prompt', enabled: import.meta.env.DEV },
  ),
)

/** Raise the pitch from anywhere, including outside React. */
export function openUpgradePrompt(request: UpgradePromptRequest): void {
  useUpgradePromptStore.getState().openUpgradePrompt(request)
}
